import React from 'react';
import { useParams } from 'react-router-dom';
import { Cat, History, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import CosImage from '../components/CosImage';
import { usePetStore } from '../store/usePetStore';
import {
  defaultPetLifeStateConfig,
  derivePetLifeInfo,
  getPetImage,
  getPetLifeStateStyle,
  getPetLifeStateText,
  getPetName,
  getPreviewImage,
  parsePetLifeStateConfig,
  type PetLifeState,
  type PetLifeStateConfig,
} from '../lib/petUtils';

type ShareStatus = {
  enabled: boolean;
  className: string;
};

type ShareLookupResponse = {
  class: {
    id: number;
    name: string;
    isAttenuationEnabled: boolean;
  };
  student: {
    id: number;
    name: string;
    currentPoints: number;
    petType: string | null;
    nickname?: string | null;
    unlockedPets?: string[] | null;
    lastFeedTime?: string | null;
    lifeState?: PetLifeState | null;
    sleepTime?: string | null;
  };
  history: {
    id: number;
    amount: number;
    reason: string | null;
    createdAt: string;
    revertedAt?: string | null;
  }[];
  settings: {
    growthConfig: string | null;
    petLifeStateConfig: string | null;
  };
};

type ActiveTab = 'history' | 'pets';

const PRESTIGE_REASON_CODE = '__PRESTIGE__';

const getErrorMessage = (err: unknown) => {
  if (!err || typeof err !== 'object') return undefined;
  const response = 'response' in err ? (err as { response?: unknown }).response : undefined;
  if (!response || typeof response !== 'object') return undefined;
  const data = 'data' in response ? (response as { data?: unknown }).data : undefined;
  if (!data || typeof data !== 'object') return undefined;
  const message = 'message' in data ? (data as { message?: unknown }).message : undefined;
  return typeof message === 'string' && message ? message : undefined;
};

const defaultGrowthConfig: Record<string, number> = {
  '0': 0,
  '1': 5,
  '2': 10,
  '3': 20,
  '4': 30,
  '5': 45,
  '6': 60,
  '7': 75,
  '8': 90,
  '9': 100,
};

const getParentShareStoredNameKey = (token: string) => `parent_share:stored_name:${token}`;

const readParentShareStoredName = (token?: string) => {
  if (!token) return '';
  try {
    return localStorage.getItem(getParentShareStoredNameKey(token)) || '';
  } catch {
    return '';
  }
};

const writeParentShareStoredName = (token: string, value: string) => {
  try {
    localStorage.setItem(getParentShareStoredNameKey(token), value);
  } catch {
    return;
  }
};

const parseGrowthConfig = (value: unknown): Record<string, number> | null => {
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const record: Record<string, number> = {};
      parsed.forEach((v, idx) => {
        const n = Number(v);
        if (Number.isFinite(n)) record[idx.toString()] = n;
      });
      if (Object.keys(record).length === 0) return null;
      record['0'] = 0;
      return record;
    }
    if (parsed && typeof parsed === 'object') {
      const record: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        const n = Number(v);
        if (Number.isFinite(n)) record[k] = n;
      }
      if (Object.keys(record).length === 0) return null;
      if (!('0' in record)) record['0'] = 0;
      return record;
    }
    return null;
  } catch {
    return null;
  }
};

const calculateLevelInfo = (currentPoints: number, growthConfig: Record<string, number>) => {
  const config = Object.keys(growthConfig).length > 0 ? growthConfig : defaultGrowthConfig;
  let level = 1;
  const maxLevel = 10;

  for (let i = 1; i <= maxLevel; i++) {
    const requiredPoints = config[i.toString()];
    if (currentPoints >= requiredPoints) {
      level = i + 1;
    } else {
      break;
    }
  }

  if (level > maxLevel) level = maxLevel;

  if (level === maxLevel) {
    return { level, progress: 100 };
  }

  const currentLevelThreshold = config[(level - 1).toString()] || 0;
  const nextLevelThreshold = config[level.toString()] || 100;

  const pointsInCurrentLevel = currentPoints - currentLevelThreshold;
  const pointsSpan = nextLevelThreshold - currentLevelThreshold;
  const progress = Math.min(100, Math.max(0, (pointsInCurrentLevel / pointsSpan) * 100));

  return { level, progress };
};

export default function ParentShare() {
  const { token } = useParams();
  const { petConfig, fetchPetConfig } = usePetStore();
  const { t } = useTranslation();

  const [statusLoading, setStatusLoading] = React.useState(true);
  const [status, setStatus] = React.useState<ShareStatus | null>(null);
  const [statusError, setStatusError] = React.useState<string>('');

  const [name, setName] = React.useState('');
  const [lookupLoading, setLookupLoading] = React.useState(false);
  const [lookupError, setLookupError] = React.useState('');
  const [data, setData] = React.useState<ShareLookupResponse | null>(null);
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('history');

  const [growthConfig, setGrowthConfig] = React.useState<Record<string, number>>(defaultGrowthConfig);
  const [petLifeConfig, setPetLifeConfig] = React.useState<PetLifeStateConfig>(defaultPetLifeStateConfig);

  React.useEffect(() => {
    fetchPetConfig();
  }, [fetchPetConfig]);

  const autoLookupAttemptedTokenRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!token) return;
    autoLookupAttemptedTokenRef.current = null;
    const storedName = readParentShareStoredName(token);
    if (storedName) setName(storedName);
  }, [token]);

  React.useEffect(() => {
    if (!token) return;
    setStatusLoading(true);
    setStatusError('');
    api
      .get(`/public/parent-share/${token}`)
      .then((res) => {
        setStatus(res.data as ShareStatus);
      })
      .catch((err: unknown) => {
        const message = getErrorMessage(err);
        setStatus(null);
        setStatusError(message || t('parentShare.invalidLink'));
      })
      .finally(() => setStatusLoading(false));
  }, [token]);

  const groupedHistory = React.useMemo(() => {
    const list = data?.history ?? [];
    const groups: Record<string, ShareLookupResponse['history']> = {};
    list.forEach((record) => {
      const date = new Date(record.createdAt).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(record);
    });
    return groups;
  }, [data]);

  const performLookup = React.useCallback(
    async (lookupName: string) => {
      if (!token) return;
      const trimmedName = lookupName.trim();
      if (!trimmedName) return;
      setLookupError('');
      setLookupLoading(true);
      setData(null);

      try {
        const res = await api.post(`/public/parent-share/${token}/lookup`, { name: trimmedName });
        const payload = res.data as ShareLookupResponse;
        setData(payload);
        setActiveTab('history');

        const nextGrowth = parseGrowthConfig(payload.settings?.growthConfig) ?? defaultGrowthConfig;
        setGrowthConfig(nextGrowth);
        setPetLifeConfig(parsePetLifeStateConfig(payload.settings?.petLifeStateConfig));

        const canonicalName = payload.student?.name || trimmedName;
        setName(canonicalName);
        writeParentShareStoredName(token, canonicalName);
      } catch (err: unknown) {
        const message = getErrorMessage(err);
        setLookupError(message || t('parentShare.lookupFailed'));
      } finally {
        setLookupLoading(false);
      }
    },
    [token, t],
  );

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    await performLookup(name);
  };

  React.useEffect(() => {
    if (!token) return;
    if (statusLoading) return;
    if (statusError) return;
    if (status && status.enabled === false) return;
    if (lookupLoading) return;
    if (data) return;
    if (autoLookupAttemptedTokenRef.current === token) return;
    const storedName = readParentShareStoredName(token);
    if (!storedName.trim()) return;
    autoLookupAttemptedTokenRef.current = token;
    void performLookup(storedName);
  }, [token, statusLoading, statusError, status, lookupLoading, data, performLookup]);

  const student = data?.student;
  const classInfo = data?.class;
  const levelInfo = student ? calculateLevelInfo(student.currentPoints, growthConfig) : null;
  const forceSleeping = classInfo?.isAttenuationEnabled !== false && (student?.currentPoints ?? 0) < 0;
  const petLifeInfo =
    student && classInfo
      ? classInfo.isAttenuationEnabled === false
        ? derivePetLifeInfo(undefined, petLifeConfig, student.lifeState ?? 'normal')
        : forceSleeping
          ? derivePetLifeInfo(undefined, petLifeConfig, 'dead')
          : derivePetLifeInfo(student.lastFeedTime ?? undefined, petLifeConfig, student.lifeState ?? 'normal')
      : null;
  const petLifeStyle = petLifeInfo ? getPetLifeStateStyle(petLifeInfo.lifeState) : null;
  const petImage = student?.petType ? getPetImage(student.petType, levelInfo?.level ?? 1, petConfig) : '';
  const unlockedSet = React.useMemo(() => {
    const set = new Set<string>();
    const list = student?.unlockedPets ?? [];
    list.forEach((id) => {
      if (typeof id === 'string' && id) set.add(id);
    });
    return set;
  }, [student?.unlockedPets]);
  const sortedPetCatalog = React.useMemo(() => {
    const list = [...petConfig];
    list.sort((a, b) => {
      const aOwned = unlockedSet.has(a.id) ? 1 : 0;
      const bOwned = unlockedSet.has(b.id) ? 1 : 0;
      if (aOwned !== bOwned) return bOwned - aOwned;
      return getPetName(a.id).localeCompare(getPetName(b.id), 'zh-CN');
    });
    return list;
  }, [petConfig, unlockedSet]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#f7fbff] flex items-start justify-center p-4">
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          backgroundImage:
            'radial-gradient(24px 24px at 12% 18%, rgba(255, 184, 213, 0.55) 0%, rgba(255, 184, 213, 0.0) 70%), radial-gradient(26px 26px at 82% 26%, rgba(255, 215, 127, 0.55) 0%, rgba(255, 215, 127, 0.0) 70%), radial-gradient(28px 28px at 70% 80%, rgba(176, 232, 255, 0.60) 0%, rgba(176, 232, 255, 0.0) 70%), radial-gradient(20px 20px at 30% 78%, rgba(186, 255, 216, 0.55) 0%, rgba(186, 255, 216, 0.0) 70%), linear-gradient(135deg, rgba(219, 234, 254, 1) 0%, rgba(255, 241, 250, 1) 45%, rgba(236, 253, 245, 1) 100%)',
        }}
      />
      <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-[#ffb5e8]/45 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-28 h-80 w-80 rounded-full bg-[#b2f7ef]/50 blur-3xl" />
      <div className="pointer-events-none absolute top-12 right-10 h-44 w-44 rounded-full bg-[#ffd166]/45 blur-3xl" />

      <div className="relative w-full max-w-xl mt-6 mb-10">
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-white/60 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-black text-gray-900 truncate">
                  {status?.className
                    ? t('parentShare.titleWithClass', { className: status.className })
                    : t('parentShare.title')}
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center shrink-0">
                <Cat className="w-5 h-5 text-pink-500" />
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6">
            {statusLoading ? (
              <div className="text-sm text-gray-500">{t('parentShare.loading')}</div>
            ) : statusError ? (
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-600">
                {statusError}
              </div>
            ) : status && status.enabled === false ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                {t('parentShare.disabledMessage')}
              </div>
            ) : !data ? (
              <form onSubmit={handleLookup} className="space-y-3">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">{t('parentShare.nameLabel')}</label>
                  <div className="relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('parentShare.namePlaceholder')}
                      className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition-all bg-gray-50 focus:bg-white"
                      autoComplete="off"
                      inputMode="text"
                    />
                  </div>
                  <div className="mt-2 text-xs text-gray-500 leading-relaxed">{t('parentShare.nameHint')}</div>
                </div>

                {lookupError ? (
                  <div className="text-sm text-red-600">{lookupError}</div>
                ) : null}

                <button
                  type="submit"
                  disabled={lookupLoading || !name.trim()}
                  className="w-full px-4 py-3 bg-pink-500 text-white rounded-xl font-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {lookupLoading ? t('parentShare.searching') : t('parentShare.submit')}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0 ${petLifeStyle?.ring ?? ''}`}>
                      {petImage ? (
                        <CosImage
                          src={petImage}
                          alt={student?.petType ?? ''}
                          className={`w-16 h-16 object-contain ${petLifeStyle?.filter ?? ''}`}
                          draggable={false}
                        />
                      ) : (
                        <div className="text-xs text-gray-400">{t('parentShare.noPet')}</div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-lg font-black text-gray-900 truncate">
                          {student?.name}
                        </div>
                        {student?.nickname ? (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                            {student.nickname}
                          </span>
                        ) : null}
                        {classInfo?.isAttenuationEnabled && petLifeInfo ? (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${petLifeStyle?.badge ?? ''}`}>
                            {getPetLifeStateText(petLifeInfo.lifeState, petLifeConfig)}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                        <span className="font-bold">{t('parentShare.points', { points: student?.currentPoints ?? 0 })}</span>
                        {levelInfo ? <span className="font-bold">Lv.{levelInfo.level}</span> : null}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setData(null);
                      setLookupError('');
                    }}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="close"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {levelInfo ? (
                  <div className="w-full">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span>{t('parentShare.currentExp')}</span>
                      <span>{Math.round(levelInfo.progress)}%</span>
                    </div>
                    <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-pink-400 to-rose-400 rounded-full"
                        style={{ width: `${levelInfo.progress}%` }}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`flex-1 px-3 py-2 rounded-xl font-bold text-sm transition-all ${activeTab === 'history' ? 'bg-pink-50 text-pink-600 border border-pink-100' : 'bg-gray-50 text-gray-600 border border-gray-100'}`}
                  >
                    <History className="w-4 h-4 inline-block mr-1.5" />
                    {t('parentShare.tabs.history')}
                  </button>
                  <button
                    onClick={() => setActiveTab('pets')}
                    className={`flex-1 px-3 py-2 rounded-xl font-bold text-sm transition-all ${activeTab === 'pets' ? 'bg-pink-50 text-pink-600 border border-pink-100' : 'bg-gray-50 text-gray-600 border border-gray-100'}`}
                  >
                    <Cat className="w-4 h-4 inline-block mr-1.5" />
                    {t('parentShare.tabs.pets')}
                  </button>
                </div>

                {activeTab === 'history' ? (
                  <div className="space-y-4">
                    {Object.keys(groupedHistory).length === 0 ? (
                      <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 text-sm text-gray-500">
                        {t('parentShare.emptyHistory')}
                      </div>
                    ) : (
                      Object.entries(groupedHistory).map(([date, items]) => (
                        <div key={date} className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                          <div className="px-4 py-2 bg-gray-50 text-xs font-bold text-gray-600">
                            {date}
                          </div>
                          <div className="divide-y divide-gray-100">
                            {items.map((record) => {
                              const isPositive = record.amount > 0;
                              const amountText = record.amount > 0 ? `+${record.amount}` : `${record.amount}`;
                              const isPrestige =
                                record.amount === 0 && record.reason === PRESTIGE_REASON_CODE;
                              const displayReason = isPrestige
                                ? t('common.prestigeReason')
                                : (record.reason || t('studentAction.unknownAction'));
                              return (
                                <div key={record.id} className="px-4 py-3 flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="text-sm font-bold text-gray-800 truncate">
                                      {displayReason}
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">
                                      {new Date(record.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                                      {record.revertedAt ? t('parentShare.revertedSuffix') : ''}
                                    </div>
                                  </div>
                                  <div className={`text-sm font-black shrink-0 ${isPrestige ? 'text-gray-400' : isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                                    {amountText}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-100 bg-white p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-bold text-gray-800">{t('parentShare.catalogTitle')}</div>
                        <div className="text-xs text-gray-500">
                          {t('parentShare.ownedCount', { count: unlockedSet.size })}
                        </div>
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                        {sortedPetCatalog.map((p) => {
                          const owned = unlockedSet.has(p.id);
                          const isCurrent = student?.petType === p.id;
                          const preview = getPreviewImage(p, true);
                          return (
                            <div
                              key={p.id}
                              className={`rounded-xl border bg-white p-2 flex flex-col items-center justify-center gap-1 transition-all ${
                                isCurrent ? 'border-pink-300 ring-2 ring-pink-200/70' : owned ? 'border-gray-200' : 'border-gray-100'
                              }`}
                            >
                              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden">
                                <CosImage
                                  src={preview}
                                  alt={getPetName(p.id)}
                                  className={`w-full h-full object-contain ${owned ? '' : 'grayscale opacity-40'}`}
                                  draggable={false}
                                />
                              </div>
                              <div className={`text-[11px] font-bold leading-tight text-center line-clamp-1 ${owned ? 'text-gray-700' : 'text-gray-400'}`}>
                                {getPetName(p.id)}
                              </div>
                              <div className={`text-[10px] leading-tight ${owned ? 'text-emerald-600' : 'text-gray-300'}`}>
                                {owned ? t('parentShare.owned') : t('parentShare.locked')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-center text-xs text-gray-400">
          {t('parentShare.footerNote')}
        </div>
      </div>
    </div>
  );
}
